import React, { useEffect, useState } from 'react';
import { Box, Button, HStack, Stack, Text, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import ConfirmModal from '../../components/ConfirmationModal';
import { deleteEvent, fetchEvent, updateEvent } from '../../hooks/calendarApi';
import { CalendarEvent } from '../../utils/types';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CalendarField,
  CalendarHero,
  CalendarPrimaryButton,
  CalendarSecondaryButton,
  CalendarSectionCard,
  CalendarShell,
  CalendarSkeleton,
  CalendarStatusBadge,
  CalendarTypeBadge,
  formatCalendarDateTime,
} from './components/calendarUi';

export default function ViewEventPage() {
  const { orgId, eventId } = useParams<{ orgId: string; eventId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !eventId) {
        return;
      }

      setLoading(true);
      try {
        const response = await fetchEvent(orgId, eventId);
        setEvent(response.data);
      } catch {
        toast({ title: 'Failed to load event', status: 'error' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [eventId, orgId, toast]);

  useEffect(() => {
    const handleNotification = async (incomingEvent: Event) => {
      const customEvent = incomingEvent as CustomEvent<{ data?: Record<string, string> }>;
      if (
        customEvent.detail?.data?.type !== 'calendar_event' ||
        customEvent.detail?.data?.event_id !== eventId ||
        !orgId ||
        !eventId
      ) {
        return;
      }

      try {
        const response = await fetchEvent(orgId, eventId);
        setEvent(response.data);
      } catch {
        // keep current screen state if background refresh fails
      }
    };

    window.addEventListener('pipeline:notification', handleNotification as EventListener);
    return () => {
      window.removeEventListener('pipeline:notification', handleNotification as EventListener);
    };
  }, [eventId, orgId]);

  const linkedSourceLabel = event?.related_task
    ? 'task'
    : event?.related_lead
      ? 'lead follow-up'
      : null;

  const handleDelete = async () => {
    if (!orgId || !eventId) {
      return;
    }

    setDeleting(true);
    try {
      await deleteEvent(orgId, eventId);
      toast({ title: 'Event deleted', status: 'success' });
      navigate(`/organization/${orgId}/calendar`);
    } catch {
      toast({ title: 'Failed to delete event', status: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (status: CalendarEvent['status']) => {
    if (!orgId || !eventId) {
      return;
    }

    setUpdatingStatus(true);
    try {
      const response = await updateEvent(orgId, eventId, { status });
      setEvent(response.data);
      toast({ title: `Event marked ${status}`, status: 'success' });
    } catch {
      toast({ title: 'Failed to update event status', status: 'error' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <CalendarShell compact>
        <CalendarSkeleton />
      </CalendarShell>
    );
  }

  if (!event) {
    return (
      <CalendarShell compact>
        <CalendarSectionCard title="Event not found" description="The requested event could not be loaded.">
          <CalendarSecondaryButton onClick={() => navigate(`/organization/${orgId}/calendar`)}>
            Back to calendar
          </CalendarSecondaryButton>
        </CalendarSectionCard>
      </CalendarShell>
    );
  }

  return (
    <CalendarShell compact>
      <CalendarHero
        eyebrow="Event"
        title={event.title}
        description={
          linkedSourceLabel
            ? `This schedule item is linked to a ${linkedSourceLabel}. Open the source record for deeper workflow changes.`
            : 'Review the timing, ownership, and CRM context of this scheduled item.'
        }
        primaryAction={
          <CalendarPrimaryButton leftIcon={<EditIcon />} onClick={() => navigate(`/organization/${orgId}/events/${event.id}/edit`)}>
            Edit event
          </CalendarPrimaryButton>
        }
        secondaryAction={
          <CalendarSecondaryButton onClick={() => navigate(`/organization/${orgId}/calendar`)}>
            Back to calendar
          </CalendarSecondaryButton>
        }
      />

      <Stack spacing={6}>
        <CalendarSectionCard
          title="Event summary"
          description="Core event details and execution context."
          action={
            <HStack spacing={2}>
              <CalendarTypeBadge type={event.event_type} />
              <CalendarStatusBadge status={event.status} />
            </HStack>
          }
        >
          <Stack spacing={5}>
            <Text color="gray.700" whiteSpace="pre-wrap">
              {event.description || 'No description was added to this event.'}
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button
                size="sm"
                borderRadius="14px"
                variant={event.status === 'upcoming' ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => handleStatusChange('upcoming')}
                isLoading={updatingStatus}
              >
                Mark upcoming
              </Button>
              <Button
                size="sm"
                borderRadius="14px"
                variant={event.status === 'completed' ? 'solid' : 'outline'}
                colorScheme="green"
                onClick={() => handleStatusChange('completed')}
                isLoading={updatingStatus}
              >
                Mark done
              </Button>
              <Button
                size="sm"
                borderRadius="14px"
                variant={event.status === 'cancelled' ? 'solid' : 'outline'}
                colorScheme="red"
                onClick={() => handleStatusChange('cancelled')}
                isLoading={updatingStatus}
              >
                Cancel event
              </Button>
            </HStack>
            <Box h="1px" bg="gray.100" />
            <HStack align="stretch" spacing={6} flexWrap="wrap">
              <CalendarField label="Starts" value={formatCalendarDateTime(event.start_datetime)} />
              <CalendarField label="Ends" value={formatCalendarDateTime(event.end_datetime)} />
              <CalendarField label="Assigned to" value={event.assigned_to_name || 'No assignee'} />
              <CalendarField label="Created by" value={event.created_by_name || 'Unknown'} />
            </HStack>
          </Stack>
        </CalendarSectionCard>

        <HStack align="stretch" spacing={6} flexWrap={{ base: 'wrap', xl: 'nowrap' }}>
          <Box flex={1} minW={{ base: '100%', xl: '0' }}>
            <CalendarSectionCard title="Linked CRM context" description="Open the source record when the event is driven by another workflow.">
              <VStack align="stretch" spacing={3}>
                {event.related_task ? (
                  <Button
                    variant="outline"
                    borderRadius="16px"
                    onClick={() => navigate(`/organization/${orgId}/tasks/${event.related_task}/view`)}
                  >
                    Open linked task
                  </Button>
                ) : null}
                {event.related_lead ? (
                  <Button
                    variant="outline"
                    borderRadius="16px"
                    onClick={() => navigate(`/organization/${orgId}/leads/${event.related_lead}`)}
                  >
                    Open linked lead
                  </Button>
                ) : null}
                {!event.related_task && !event.related_lead ? (
                  <Text fontSize="sm" color="gray.500">
                    This event is not linked to a task or lead record.
                  </Text>
                ) : null}
              </VStack>
            </CalendarSectionCard>
          </Box>

          <Box flex={1} minW={{ base: '100%', xl: '0' }}>
            <CalendarSectionCard title="Record details" description="Metadata for review and audit.">
              <VStack align="stretch" spacing={4}>
                <CalendarField label="Created at" value={formatCalendarDateTime(event.created_at)} />
                <CalendarField label="Updated at" value={formatCalendarDateTime(event.updated_at)} />
                <CalendarField label="Event id" value={event.id} />
              </VStack>
            </CalendarSectionCard>
          </Box>
        </HStack>

        {!event.related_task && !event.related_lead ? (
          <CalendarSectionCard title="Danger zone" description="Delete only if this event should be removed permanently.">
            <Button
              leftIcon={<DeleteIcon />}
              variant="outline"
              colorScheme="red"
              borderRadius="16px"
              onClick={onOpen}
              isLoading={deleting}
            >
              Delete event
            </Button>
          </CalendarSectionCard>
        ) : null}
      </Stack>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={deleting}
        AreYouSureQuestion="Delete event?"
        AreYouSureTextExplanation={`This will permanently remove ${event.title}.`}
        confirmButtonText="Delete event"
        confirmButtonColor="#DC2626"
        colorScheme="red"
      />
    </CalendarShell>
  );
}
